// /help: public (people booking a time have no account), so it never touches Cognito.
// Keep the plan numbers here in step with services/api/src/lib/tenant.ts (PLANS).
import { useEffect, type ReactNode } from "react";
import { ArrowRight, Building2, CalendarDays, CircleHelp, CreditCard, KeyRound, Link2, LifeBuoy, Plug, Users } from "lucide-react";
import { Lockup, Logo } from "../components/icons";

const SECTIONS = [
  { id: "start", label: "Get started", icon: CalendarDays },
  { id: "calendars", label: "Connect your calendars", icon: Plug },
  { id: "links", label: "Booking links", icon: Link2 },
  { id: "group", label: "Group links (co-hosts)", icon: Users },
  { id: "meetings", label: "Changing meetings", icon: CalendarDays },
  { id: "company", label: "Your company account", icon: Building2 },
  { id: "billing", label: "Plans and billing", icon: CreditCard },
  { id: "sso", label: "Single sign-on (Entra ID)", icon: KeyRound },
  { id: "troubleshooting", label: "Troubleshooting", icon: LifeBuoy },
  { id: "plans", label: "Plans at a glance", icon: CircleHelp },
] as const;

export default function Help() {
  useEffect(() => {
    document.title = "Help · eBookMe";
    // Deep links (/help#calendars) land on the right section once rendered.
    if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
  }, []);

  return (
    <div className="min-h-full flex flex-col bg-bg">
      <div className="brand-bar h-1 shrink-0" aria-hidden />
      <header className="bg-surface border-b border-line px-4 h-14 flex items-center gap-3 shrink-0 sticky top-0 z-10">
        <a href="/" className="flex items-center gap-3" aria-label="eBookMe home">
          <Logo className="size-7 sm:hidden" />
          <span className="max-sm:hidden">
            <Lockup height={17} />
          </span>
        </a>
        <span className="text-sm text-muted">Help</span>
        <a href="/" className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-accent-text hover:underline underline-offset-2">
          Open eBookMe <ArrowRight className="size-4" />
        </a>
      </header>

      <div className="flex-1 w-full max-w-6xl mx-auto px-4 py-8 lg:grid lg:grid-cols-[14rem_minmax(0,1fr)] lg:gap-10">
        <nav aria-label="Help sections" className="max-lg:mb-8">
          <div className="lg:sticky lg:top-24">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-2">On this page</div>
            <ul className="flex lg:flex-col flex-wrap gap-1">
              {SECTIONS.map(({ id, label, icon: Icon }) => (
                <li key={id}>
                  <a href={`#${id}`} className="flex items-center gap-2 rounded-lg px-2.5 h-9 text-sm text-ink-2 hover:bg-surface-2 max-lg:border max-lg:border-line max-lg:bg-surface">
                    <Icon className="size-4 text-accent-text shrink-0" /> {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <main className="min-w-0 max-w-3xl">
          <h1 className="text-3xl font-bold">eBookMe help</h1>
          <p className="text-ink-2 mt-3 leading-relaxed">
            eBookMe lets people share a booking link so clients can pick a time when they, and any colleagues on the link, are free across all their calendars. It checks Outlook and Google calendars together, adds the meeting with a Teams,
            Webex or dial-in call, and emails everyone. Clients don't need an account, and they can reschedule or cancel from their email.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            <Shortcut href="#start" title="I want to share my availability" text="Connect a calendar, create a link, share it." />
            <Shortcut href="#company" title="I set up eBookMe for my company" text="Invite colleagues, choose a plan, add your branding." />
          </div>

          <Section id="start" title="Get started">
            <Steps
              items={[
                <>
                  Sign in at your company's eBookMe address (for example <code>yourcompany.ebookme.app</code>). New to eBookMe? <a href="https://app.ebookme.app/signup">Create your company</a>; it's ready at once with 14 days of Standard.
                </>,
                <>
                  Open <b>Calendars</b> and connect the calendar where meetings should go. Add any others you use, so busy time on them is blocked too.
                </>,
                <>
                  Open <b>Settings</b> and check your time zone. Add your Webex personal room link or dial-in number if you'll offer those.
                </>,
                <>
                  Open <b>Booking links</b>, select <b>+ New link</b>, give it a title, pick the lengths you offer and your available hours, and save.
                </>,
                <>
                  Select <b>Share</b> to copy the link (it looks like <code>yourcompany.ebookme.app/b/your-name-intro-call</code>) and send it to clients or add it to your email signature.
                </>,
              ]}
            />
          </Section>

          <Section id="calendars" title="Connect your calendars">
            <p>Add as many accounts as you use. eBookMe combines busy time from every calendar you tick, so a client can never book over something already in any of them.</p>
            <dl className="mt-4 space-y-3">
              <Def term="+ Outlook · work or school">Your company's Microsoft 365 account. eBookMe checks busy time, adds booked meetings and creates Teams meetings.</Def>
              <Def term="+ Outlook · other organization">A client's or partner's Microsoft 365 account you also work in. Availability only: eBookMe reads event times, not their details, and never writes to it.</Def>
              <Def term="+ Outlook.com (personal)">A personal Microsoft account. Busy time and booked meetings.</Def>
              <Def term="+ Google Calendar">A Google account (personal or Workspace). Busy time, and booked meetings on calendars you own.</Def>
            </dl>
            <p className="mt-4">Under each account, tick the calendars to check for conflicts. Shared or team calendars you don't want to block time can stay unticked.</p>
            <Callout title="If an account shows Reconnect needed">Its access expired or was revoked. Select <b>Reconnect</b> and sign in with that account again; your ticked calendars are kept. Until then its busy time isn't blocking your links.</Callout>
          </Section>

          <Section id="links" title="Booking links">
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <b>Lengths</b>: offer one length or several (for example 15, 30 and 60 minutes). With several, clients choose, and open times are worked out for the length they pick. Several lengths are part of Standard and Teams.
              </li>
              <li>
                <b>Where meetings go</b>: the calendar that receives bookings and sends the invitation. Availability-only accounts can't be chosen here.
              </li>
              <li>
                <b>Meeting type</b>: Microsoft Teams (created automatically), Webex (your personal room from Settings) or a conference call (your dial-in from Settings).
              </li>
              <li>
                <b>Available hours</b>: per weekday, in the link's time zone. Clients see the times converted to their own time zone.
              </li>
              <li>
                <b>Reminders</b>: emails to the client and their guests, for example 24 hours and 1 hour before. They move if the meeting is rescheduled and stop if it's canceled. Part of Standard and Teams.
              </li>
              <li>
                <b>Scheduling rules</b>: start times every N minutes, minimum notice, buffers before and after, and how many days ahead people can book.
              </li>
            </ul>
            <p className="mt-3">Turn a link off to stop new bookings without deleting it. A link's address can't change after it's saved.</p>
          </Section>

          <Section id="group" title="Group links (co-hosts)">
            <p>
              On the Teams plan, add colleagues to a link under <b>Co-hosts</b>. Clients then see only times when everyone is free, and every host gets the invitation. A colleague appears in the list once they've signed in to eBookMe; ask them to
              connect their calendar too.
            </p>
          </Section>

          <Section id="meetings" title="Changing meetings">
            <p>
              <b>Meetings</b> lists what's booked with you. Open one to reschedule (pick a new time; everyone gets an updated invitation and the Teams meeting moves with it) or cancel (with an optional reason). Each meeting keeps a history of
              changes.
            </p>
            <p className="mt-3">Clients reschedule or cancel from the links in their confirmation email. Each change replaces the link, so the newest email always has the one that works.</p>
          </Section>

          <Section id="company" title="Your company account">
            <p>Admins see <b>Company</b> in the menu:</p>
            <dl className="mt-3 space-y-3">
              <Def term="People">Invite colleagues by email, change roles, resend invitations, turn people off or remove them. Members share their own links; admins also manage the company; owners also manage other owners.</Def>
              <Def term="Plan & billing">Choose Standard or Teams, manage the subscription, cards and invoices.</Def>
              <Def term="Single sign-on">Verify your email domain and connect Microsoft Entra ID (Teams plan).</Def>
              <Def term="Branding & settings">Your logo, colour and display name on booking pages and emails (Standard and Teams), and the company time zone new people start with.</Def>
              <Def term="Audit log">Who changed what in the company account.</Def>
            </dl>
          </Section>

          <Section id="billing" title="Plans and billing">
            <p>
              Every new company gets 14 days of Standard, no card needed. Afterwards, choose a plan under <b>Company → Plan &amp; billing</b>, or stay on Free. Paid plans are billed yearly per person through Stripe; adding or removing people
              adjusts the seats automatically (prorated). <b>Manage subscription</b> opens Stripe to switch plans, change the card, download invoices or cancel.
            </p>
            <p className="mt-3">
              Moving to a smaller plan never deletes anything. If you have more links than the plan allows, the newest ones are paused (marked <i>Paused by plan</i>) until you upgrade.
            </p>
          </Section>

          <Section id="sso" title="Single sign-on (Entra ID)">
            <Steps
              items={[
                <>
                  Under <b>Company → Single sign-on</b>, add your email domain and create the DNS TXT record shown, then select <b>Verify</b>.
                </>,
                <>Follow the on-screen steps to register eBookMe in your Entra admin center (OpenID Connect or SAML) and paste in the details.</>,
                <>
                  Select <b>Test</b> and sign in. When the test passes, people with your domain sign in with Microsoft. You can require single sign-on and let anyone in your directory join automatically.
                </>,
              ]}
            />
          </Section>

          <Section id="troubleshooting" title="Troubleshooting">
            <Faq q="Google says it hasn't verified the app">
              <p>While Google reviews eBookMe, select <b>Advanced → Go to eBookMe</b>. eBookMe only reads busy times and adds meetings booked through it.</p>
            </Faq>
            <Faq q="Microsoft says I need admin approval">
              <p>That organization only lets its IT team approve apps. Ask them to approve "eBookMe"; it asks for calendar access only.</p>
            </Faq>
            <Faq q="The booking page says it's temporarily unavailable">
              <p>eBookMe couldn't read a host's calendar. Each host on the link should check <b>Calendars</b> for a connection that needs reconnecting.</p>
            </Faq>
            <Faq q="A time I'm busy still shows as open">
              <p>Make sure the calendar with that event is ticked under its account on the Calendars page, and the event is marked Busy (not Free).</p>
            </Faq>
            <Faq q="Teams, Webex or the dial-in is missing from an invitation">
              <p>Teams needs the link's destination to be a Microsoft work or school account. Webex needs your personal room link, and a conference call needs your dial-in number, in Settings.</p>
            </Faq>
            <Faq q="What can eBookMe see in my calendar?">
              <p>For availability: only the start and end times of events. To book: it creates the meeting on the calendar you chose and can change or cancel only meetings it created. It never reads email.</p>
            </Faq>
            <p className="mt-6">
              Still stuck? Email <a href="mailto:support@ebookme.app">support@ebookme.app</a> with a screenshot.
            </p>
          </Section>

          <Section id="plans" title="Plans at a glance">
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-sm border border-line rounded-xl overflow-hidden">
                <thead className="bg-surface-2 text-left">
                  <tr>
                    <th className="px-3 py-2" />
                    <th className="px-3 py-2">Free</th>
                    <th className="px-3 py-2">Standard</th>
                    <th className="px-3 py-2">Teams</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line bg-surface">
                  {[
                    ["Price per person", "$0", "$30 / year", "$48 / year"],
                    ["People", "1", "Unlimited", "Unlimited"],
                    ["Booking links", "1", "Unlimited", "Unlimited"],
                    ["Calendars per person", "1", "Unlimited", "Unlimited"],
                    ["Several lengths, reminders", "—", "✓", "✓"],
                    ["Your branding", "—", "✓", "✓"],
                    ["Group links (co-hosts)", "—", "—", "✓"],
                    ["Single sign-on", "—", "—", "✓"],
                  ].map(([k, ...v]) => (
                    <tr key={k}>
                      <th className="px-3 py-2 text-left font-medium">{k}</th>
                      {v.map((x, i) => (
                        <td key={i} className="px-3 py-2">
                          {x}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </main>
      </div>
      <footer className="border-t border-line bg-surface px-4 py-4 text-xs text-muted text-center">
        © {new Date().getFullYear()} eBookMe · <a href="https://ebookme.app/terms">Terms</a> · <a href="https://ebookme.app/privacy">Privacy</a>
      </footer>
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-20 mt-12 [&_a]:text-accent-text [&_a]:underline [&_a]:underline-offset-2">
      <h2 className="display text-xl font-bold text-ink mb-3 pb-2 border-b border-line">{title}</h2>
      <div className="text-ink-2 leading-relaxed">{children}</div>
    </section>
  );
}

function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="mt-4 space-y-3">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span className="shrink-0 size-6 rounded-full bg-accent text-accent-ink text-xs font-semibold flex items-center justify-center mt-0.5 tabular">{i + 1}</span>
          <div>{it}</div>
        </li>
      ))}
    </ol>
  );
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="group mt-3 rounded-xl border border-line bg-surface px-4 py-3 [&[open]]:pb-4">
      <summary className="cursor-pointer list-none font-medium text-ink flex items-start gap-2">
        <span className="text-accent-text transition-transform group-open:rotate-90 mt-px" aria-hidden>
          ›
        </span>
        {q}
      </summary>
      <div className="mt-2 pl-4 text-sm">{children}</div>
    </details>
  );
}

function Def({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div>
      <dt className="font-medium text-ink">{term}</dt>
      <dd className="text-ink-2">{children}</dd>
    </div>
  );
}

function Callout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-6 rounded-xl bg-warn-soft px-4 py-3 text-sm">
      <div className="font-semibold text-ink">{title}</div>
      <div className="mt-1 text-ink-2">{children}</div>
    </div>
  );
}

function Shortcut({ href, title, text }: { href: string; title: string; text: string }) {
  return (
    <a href={href} className="group block rounded-xl border border-line bg-surface p-4 hover:border-accent no-underline">
      <div className="font-semibold text-ink flex items-center gap-1">
        {title} <ArrowRight className="size-4 text-accent-text transition-transform group-hover:translate-x-0.5" />
      </div>
      <div className="text-sm text-muted mt-1">{text}</div>
    </a>
  );
}
